import { Link, useSearchParams } from "react-router-dom";
import { useEffect } from "react";
import { SiteShell } from "@/components/site/SiteShell";
import { SEO, breadcrumbSchema } from "@/components/site/SEO";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle2, Calendar, Mail, Phone } from "lucide-react";
import { SITE, CALENDLY_URL } from "@/lib/siteData";
import { gaEvent, metaEvent } from "@/lib/analytics";

const SOURCE_LABELS = {
  "contact-cta": "Homepage / Contact page brief",
  "pricing-page": "Pricing brief",
  "hotels-landing": "Hotels & Lodges brief",
  "agencies-landing": "Agencies brief",
  "medical-landing": "Medical / Dental / Pharma brief",
  "construction-landing": "Construction brief",
};

export default function ThankYou() {
  const [params] = useSearchParams();
  const source = params.get("source") || "unknown";
  const label = SOURCE_LABELS[source] || "your brief";

  // Fire a dedicated "thank_you_view" event on landing — enables Ads/Meta
  // retargeting audiences to be built purely off this URL/event combination.
  // gaEvent / metaEvent are stable module-level functions but we include them
  // in the deps to keep exhaustive-deps happy.
  useEffect(() => {
    gaEvent("thank_you_view", { lead_source: source });
    metaEvent("Contact", { lead_source: source });
  }, [source, gaEvent, metaEvent]);

  return (
    <SiteShell>
      <SEO
        title="Thank you — brief received | Pie in the Sky Productions"
        description="Thanks for your brief. We'll be in touch within one business day. In the meantime, book a 15-minute call or explore our portfolio."
        path="/thank-you"
        // noindex the confirmation page so it doesn't appear in search
        noindex
        jsonLd={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Thank you" },
        ])}
      />
      <section className="section-pad pt-32 sm:pt-40">
        <div className="container-site max-w-3xl">
          <div
            data-testid="thank-you-hero"
            className="rounded-2xl border border-[hsl(var(--primary))/0.35] bg-[hsl(225,18%,8%)] p-8 sm:p-12 text-center"
          >
            <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-[hsl(var(--primary))/0.12] border border-[hsl(var(--primary))/0.4] text-[hsl(var(--primary))] mb-6">
              <CheckCircle2 className="h-6 w-6" />
            </span>
            <p className="kicker mb-3">Brief received</p>
            <h1 className="font-display uppercase text-foreground text-[clamp(2rem,5vw,3.75rem)] leading-[0.95] tracking-[0.01em] text-balance">
              Thank you — we&apos;ve got your <span className="text-[hsl(var(--primary))]">{label}</span>.
            </h1>
            <p className="mt-6 text-sm sm:text-base text-muted-foreground leading-relaxed max-w-xl mx-auto">
              Our team will review it and reply within <strong className="text-foreground">one business day</strong> from
              {" "}<a href={`mailto:${SITE.email}`} className="text-[hsl(var(--primary))] hover:underline">{SITE.email}</a>.
              If you&apos;d like to speed things up, grab a 15-minute discovery call — most projects kick off within a week of that call.
            </p>

            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Button
                asChild
                data-testid="thank-you-calendly-cta"
                className="h-12 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:brightness-110 px-6"
              >
                <a href={CALENDLY_URL} target="_blank" rel="noreferrer noopener">
                  <Calendar className="mr-2 h-4 w-4" />Book a 15-min discovery call
                </a>
              </Button>
              <Button
                asChild
                variant="ghost"
                data-testid="thank-you-portfolio-cta"
                className="h-12 rounded-full border border-border bg-card/60 hover:bg-card px-5"
              >
                <Link to="/portfolio">See our portfolio <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </div>
          </div>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <a
              href={`mailto:${SITE.email}`}
              data-testid="thank-you-email-link"
              className="rounded-xl border border-border bg-card p-5 hover:border-[hsl(var(--primary))/0.5] transition-colors"
            >
              <Mail className="h-5 w-5 text-[hsl(var(--primary))]" />
              <p className="mt-3 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Email</p>
              <p className="mt-1 text-sm text-foreground truncate">{SITE.email}</p>
            </a>
            <a
              href={`tel:${SITE.phone.replace(/\s+/g, "")}`}
              data-testid="thank-you-phone-link"
              className="rounded-xl border border-border bg-card p-5 hover:border-[hsl(var(--primary))/0.5] transition-colors"
            >
              <Phone className="h-5 w-5 text-[hsl(var(--primary))]" />
              <p className="mt-3 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Phone</p>
              <p className="mt-1 text-sm text-foreground">{SITE.phone}</p>
            </a>
            <Link
              to="/case-studies"
              data-testid="thank-you-case-studies-link"
              className="rounded-xl border border-border bg-card p-5 hover:border-[hsl(var(--primary))/0.5] transition-colors"
            >
              <ArrowRight className="h-5 w-5 text-[hsl(var(--primary))]" />
              <p className="mt-3 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">While you wait</p>
              <p className="mt-1 text-sm text-foreground">Read our case studies</p>
            </Link>
          </div>
        </div>
      </section>
    </SiteShell>
  );
}
